/** Shared display helpers. They never mutate records or translate user-written text. */
export function summarizeHouseholdIncome(family = {}, residenceKey = 'livesWithStudent') {
  const data = family || {};
  const people = [data.responsible1, data.responsible2,
    ...(Array.isArray(data.members) ? data.members : [])].filter(Boolean);
  const residents = people.filter((person) => person[residenceKey] === true);
  const totalIncomeCents = residents.reduce((sum, person) => {
    const cents = Number(person.incomeCents || 0);
    return sum + (Number.isFinite(cents) ? Math.round(cents) : 0);
  }, 0);
  const residentCount = residents.length + 1; // Always include the student/attendee.
  return { totalIncomeCents, coResidentCount: residents.length, residentCount,
    perCapitaIncomeCents: Math.round(totalIncomeCents / residentCount) };
}

const fieldLabels = {
  id: 'Identificador', registrationNumber: 'N\u00ba de matr\u00edcula', ra: 'RA',
  fullName: 'Nome completo', socialName: 'Nome social', photoUrl: 'Foto',
  enrollmentDate: 'Data de inscri\u00e7\u00e3o', birthDate: 'Data de nascimento', age: 'Idade',
  gender: 'G\u00eanero', ethnicity: 'Etnia/cor', nationality: 'Nacionalidade', nis: 'NIS',
  whatsapp: 'WhatsApp', phone: 'Telefone', phone1: 'Telefone 1', phone2: 'Telefone 2', email: 'E-mail',
  cpf: 'CPF', rg: 'RG', exitDate: 'Data de sa\u00edda', exitReason: 'Motivo de sa\u00edda',
  status: 'Status', classId: 'Turma', className: 'Turma', classLinkedAt: 'Data de v\u00ednculo com a turma',
  family: 'Fam\u00edlia', responsible1: 'Respons\u00e1vel 1', responsible2: 'Respons\u00e1vel 2',
  members: 'Integrantes', name: 'Nome', relationship: 'Parentesco', profession: 'Profiss\u00e3o',
  workplace: 'Local de trabalho', educationLevel: 'Escolaridade', incomeCents: 'Renda',
  livesWithStudent: 'Reside com o aluno', livesWithAttendee: 'Reside com o atendido',
  address: 'Endere\u00e7o', zipCode: 'CEP', street: 'Rua', number: 'N\u00famero', complement: 'Complemento',
  neighborhood: 'Bairro', city: 'Cidade', state: 'Estado', zone: 'Zona de resid\u00eancia', housingType: 'Tipo de moradia',
  social: 'Social', cras: 'CRAS', familyIncome: 'Renda familiar', benefits: 'Benef\u00edcios', value: 'Valor',
  education: 'Escolaridade', school: 'Escola', level: 'Escolaridade', grade: 'S\u00e9rie/Ano', period: 'Per\u00edodo',
  extraCourses: 'Cursos extras', institution: 'Institui\u00e7\u00e3o', courseHistory: 'Hist\u00f3rico de cursos',
  groupHistory: 'Hist\u00f3rico de grupos', courseId: 'Curso', groupId: 'Grupo',
  startDate: 'Data inicial', endDate: 'Data final', health: 'Sa\u00fade', answer: 'Resposta', details: 'Detalhes',
  allergy: 'Alergia', healthIssue: 'Problema de sa\u00fade', foodRestriction: 'Restri\u00e7\u00e3o alimentar',
  controlledMedication: 'Medica\u00e7\u00e3o controlada', activitySupport: 'Acompanhamento nas atividades',
  physicalLimitation: 'Limita\u00e7\u00e3o f\u00edsica', psychoactiveSubstances: 'Subst\u00e2ncias psicoativas', disability: 'Defici\u00eancia',
  customFields: 'Campos personalizados', files: 'Arquivos', categories: 'Categorias', documents: 'Documentos',
  fileName: 'Nome do arquivo', category: 'Categoria', type: 'Tipo', size: 'Tamanho', title: 'T\u00edtulo',
  source: 'Origem', formFieldLabel: 'Campo do formul\u00e1rio', dataUrl: 'Arquivo', attachment: 'Anexo',
  attachmentId: 'Anexo vinculado', attachmentName: 'Nome do anexo', confidential: 'Confidencial', notes: 'Observa\u00e7\u00f5es',
  observations: 'Observa\u00e7\u00f5es', authorizations: 'Autoriza\u00e7\u00f5es', pickupPersons: 'Pessoas autorizadas',
  __absenceJustifications: 'Atestados e aus\u00eancias', createdAt: 'Criado em', updatedAt: 'Atualizado em',
  uploadedAt: 'Enviado em', date: 'Data', time: 'Hor\u00e1rio', shift: 'Turno', severity: 'Gravidade',
  description: 'Descri\u00e7\u00e3o', createdByName: 'Cadastrado por', updatedByName: 'Atualizado por',
  changedAt: 'Alterado em', changedByName: 'Alterado por', createdBy: 'Cadastrado por', updatedBy: 'Atualizado por',

"createdByUserId": "Código do usuário que cadastrou",
"updatedByUserId": "Código do usuário que alterou",
"deletedByUserId": "Código do usuário que excluiu",
"uploadedByUserId": "Código do usuário que enviou",
"changedByUserId": "Código do usuário que alterou",
"userId": "Código do usuário",
"studentId": "Código do aluno",
"attendeeId": "Código do atendido",
"teacherId": "Professor responsável",
"teacherIds": "Professores responsáveis",
"absenceJustificationId": "Justificativa vinculada",
"justificationStartDate": "Início da justificativa",
"justificationEndDate": "Fim da justificativa",
"deletedAt": "Excluído em",
"deletedByName": "Excluído por",
"uploadedByName": "Enviado por",
"restoredAt": "Restaurado em",
"restoredByUserId": "Código do usuário que restaurou",
"fileId": "Código do arquivo",
"filePath": "Caminho do arquivo",
"originalName": "Nome original",
"mimeType": "Tipo do arquivo",
"contentType": "Tipo do conteúdo",
"url": "Endereço do arquivo",
"downloadUrl": "Endereço para baixar",
"uploadedBy": "Enviado por",
"sourceKey": "Identificador da origem",
"sourceId": "Código da origem",
"sourceType": "Tipo de origem",
"externalId": "Código externo",
"integrationId": "Código da integração",
"integrationSource": "Origem da integração",
"syncedAt": "Sincronizado em",
"processId": "Código do processo",
"processInstanceId": "Código da instância do processo",
"formId": "Código do formulário",
"coordinator": "Coordenador",
"courseName": "Curso",
"groupName": "Curso",
"previousClassId": "Turma anterior",
"previousClassName": "Nome da turma anterior",
"newClassId": "Nova turma",
"previousStatus": "Status anterior",
"autoGenerated": "Gerado automaticamente",
"automatic": "Automático",
"reason": "Motivo",
"active": "Ativo",
"enabled": "Habilitado",
"isActive": "Ativo",
"isDeleted": "Excluído",
"lessonTheme": "Tema da aula",
"objective": "Objetivo",
"entries": "Registros",
"attendanceId": "Código da frequência",
"totalIncomeCents": "Renda total",
"perCapitaIncomeCents": "Renda per capita",
"residentCount": "Pessoas na residência",
"coResidentCount": "Familiares residentes",
"__courseHistoryMerged": "Histórico de cursos consolidado",
};
const actions = { CREATE: 'Cadastro criado', UPDATE: 'Cadastro editado', DELETE: 'Cadastro exclu\u00eddo',
  AUTO_INACTIVATE: 'Baixa autom\u00e1tica', IMPORT: 'Cadastro importado', SYNC: 'Cadastro sincronizado',
  INTEGRATION_SYNC: 'Atualiza\u00e7\u00e3o por integra\u00e7\u00e3o', RESTORE: 'Cadastro restaurado' };
const values = {
  status: { ACTIVE: 'Ativo', INACTIVE: 'Inativo', IN_PROCESS: 'Em processo', PLANNED: 'Planejado',
    PRESENT: 'Presente', ABSENT: 'Ausente', JUSTIFIED: 'Justificado' },
  type: { MEDICAL_CERTIFICATE: 'Atestado', JUSTIFIED_ABSENCE: 'Falta justificada' },
  severity: { LIGHT: 'Leve', MODERATE: 'Moderada', SEVERE: 'Grave' },
  source: { form: 'Formulário', manual: 'Manual', integration: 'Integração', 'absence-justification': 'Atestado ou ausência justificada', 'class-change': 'Troca de turma', 'class-transfer': 'Transferência de turma', automatic: 'Automático', system: 'Sistema', import: 'Importação', FORM: 'Formulário', MANUAL: 'Manual', INTEGRATION: 'Integração', SYSTEM: 'Sistema' },
};
const owns = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
export const personHistoryActionLabel = (action) => owns(actions, action) ? actions[action] : 'Altera\u00e7\u00e3o registrada';
export const normalizeHistorySearch = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR');
export function personHistoryPathLabel(path) {
  // Custom field titles are user content, not internal identifiers.
  const parts = String(path || '').split('.').filter(Boolean);
  return parts.map((part, index) => {
    if (parts[0] === 'customFields' && index > 0 && !/^__absenceJustifications(?: |$)/.test(parts[1] || '')) return part;
    const indexed = part.match(/^(.*?)\s+(\d+)$/);
    if (indexed && owns(fieldLabels, indexed[1])) return `${fieldLabels[indexed[1]]} ${indexed[2]}`;
    return owns(fieldLabels, part) ? fieldLabels[part] : part;
  }).join(' \u203a ');
}
export function formatPersonHistoryValue(value, field = '') {
  if (value === null || value === undefined || value === '') return '\u2014';
  if (typeof value === 'boolean') return value ? 'Sim' : 'N\u00e3o';
  const key = String(field).split('.').pop();
  if (key === 'incomeCents') {
    const cents = Number(value);
    return Number.isFinite(cents) ? (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : String(value);
  }
  if (owns(values, key) && owns(values[key], value)) return values[key][value];
  if (/(?:Date|At)$/.test(key) || key === 'date') {
    const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;
    if (/^\d{4}-\d{2}-\d{2}T/.test(String(value))) {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) return date.toLocaleString('pt-BR');
    }
  }
  if (Array.isArray(value)) return value.length ? value.map((item) => formatPersonHistoryValue(item, field)).join('; ') : '\u2014';
  if (typeof value === 'object') return Object.keys(value).length
    ? Object.entries(value).map(([name, content]) => `${personHistoryPathLabel(name)}: ${formatPersonHistoryValue(content, name)}`).join('; ')
    : '\u2014';
  return String(value);
}

/* audit-portuguese-metadata-v2 */
